import { Box, Typography, Divider } from "@mui/material";
import TextInput from "../../../../components/textinput/textinput";
import { useState } from "react";
import "./add-inspection-firm.css";
import { phoneFormatInput } from "../../../../utils/Formatter";
import DialogContainer from "../../../../components/dialog-container/dialog-container";
import { useFormik } from "formik";
import * as yup from "yup";
import ErrorNotification from "../../../../components/error-notification/error-notification";
import Loading from "../../../../components/loading/loading";
import { emailRegex, websiteRegex } from "../../../../utils/Constant";
import { addInspectionFirmRequest } from "../../../../utils/APICalls";

type props = {
  showDialog: boolean;
  closeDialog: any;
  addFirm: any;
};

type FormValues = {
  displayName: string;
  aliasName?: string;
  website: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

const ValidationSchema = yup.object({
  displayName: yup.string().required("This field can't be empty"),
  website: yup.string().matches(websiteRegex, "Invalid Website"),
  firstName: yup.string().required("This field can't be empty"),
  lastName: yup.string().required("This field can't be empty"),
  email: yup
    .string()
    .required("This field can't be empty")
    .matches(emailRegex, "Invalid Email Address"),
  phone: yup.string().required("This field can't be empty"),
});

export default function AddInspectionFirmDialog({
  showDialog,
  closeDialog,
  addFirm,
}: props) {
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const formik = useFormik<FormValues>({
    initialValues: {
      displayName: "",
      aliasName: "",
      website: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    },
    validationSchema: ValidationSchema,
    onSubmit: (values) => {
      addInspectionFirm(values);
    },
  });

  const onPhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(event);
    formik.setFieldValue("phone", phoneFormatInput(event.target.value));
  };

  const addInspectionFirm = async (data: any) => {
    if (!isPhoneValid()) {
      return;
    }
    let finalWebsite = null;
    if (data.website.length > 0) {
      finalWebsite = data.website.includes("http")
        ? data.website
        : `https://${data.website}`;
    }
    const payload = {
      displayName: data.displayName,
      aliasName: data.aliasName,
      website: finalWebsite,
      primaryContact: {
        emailAddress: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNumber: data.phone,
      },
    };
    setShowLoading(true);
    const response = await addInspectionFirmRequest(payload);
    setShowLoading(false);
    if (response?.status === 201) {
      addFirm(response?.data);
      hideDialog();
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const normalizePhone = (phone: string) => phone.replace(/\D/g, "");

  const isPhoneValid = () => {
    const normalized = normalizePhone(formik.values.phone);
    // Require exactly 10 digits
    if (normalized.length > 0 && normalized.length !== 10) {
      formik.setFieldError("phone", "Incorrect phone");
      return false;
    }
    return true;
  };

  const hideDialog = () => {
    formik.resetForm();
    closeDialog();
  };

  return (
    <DialogContainer
      title="Add Inspection Firm"
      showDialog={showDialog}
      closeDialog={hideDialog}
      buttonText="Add Inspection Firm"
      onSubmit={formik.handleSubmit}
    >
      <form onSubmit={formik.handleSubmit}>
        <Typography variant="h5">Inspection Firm Details</Typography>
        <Box className="field-layout">
          <Typography className="field-label-txt">
            Inspection Firm Name
          </Typography>
          <TextInput
            className="field-input"
            id="inspectionFirmName"
            name="displayName"
            label="Inspection Firm Name"
            variant="outlined"
            value={formik.values.displayName}
            onChange={formik.handleChange}
            error={
              formik.touched.displayName && Boolean(formik.errors.displayName)
            }
            helperText={formik.touched.displayName && formik.errors.displayName}
          />
        </Box>
        <Divider />
        <Box className="field-layout">
          <Typography className="field-label-txt">
            Inspection Firm Alias <br />
            (Optional)
          </Typography>
          <TextInput
            className="field-input"
            id="aliasName"
            name="aliasName"
            variant="outlined"
            label="Inspection Firm Alias"
            value={formik.values.aliasName}
            onChange={formik.handleChange}
          />
        </Box>
        <Divider />
        <Box className="field-layout">
          <Typography className="field-label-txt">Website</Typography>
          <TextInput
            className="field-input"
            id="website"
            name="website"
            label="Website"
            variant="outlined"
            value={formik.values.website}
            onChange={formik.handleChange}
            error={formik.touched.website && Boolean(formik.errors.website)}
            helperText={formik.touched.website && formik.errors.website}
          />
        </Box>

        <Typography variant="h5" sx={{ mt: 2 }}>
          Primary Account Manager/Admin
        </Typography>
        <Box className="field-layout">
          <Typography className="field-label-txt">Name</Typography>
          <Box sx={{ display: "flex", width: "calc(100% - 180px)" }}>
            <TextInput
              sx={{ flex: 1, mr: 2 }}
              id="firstName"
              name="firstName"
              label="First Name"
              variant="outlined"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              error={
                formik.touched.firstName && Boolean(formik.errors.firstName)
              }
              helperText={formik.touched.firstName && formik.errors.firstName}
            />
            <TextInput
              sx={{ flex: 1 }}
              id="lastName"
              name="lastName"
              label="Last Name"
              variant="outlined"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={formik.touched.lastName && formik.errors.lastName}
            />
          </Box>
        </Box>
        <Divider />
        <Box className="field-layout">
          <Typography className="field-label-txt">Email</Typography>
          <TextInput
            className="field-input"
            id="email"
            name="email"
            label="Email"
            variant="outlined"
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
        </Box>
        <Divider />
        <Box className="field-layout">
          <Typography className="field-label-txt">Phone</Typography>
          <TextInput
            className="field-input"
            id="phone"
            name="phone"
            label="Phone"
            variant="outlined"
            value={formik.values.phone}
            onChange={onPhoneChange}
            error={formik.touched.phone && Boolean(formik.errors.phone)}
            helperText={formik.touched.phone && formik.errors.phone}
          />
        </Box>
      </form>
      {showLoading && <Loading />}
      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />
    </DialogContainer>
  );
}
